import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, MapPin, Wifi, BadgeCheck, FlaskConical, DollarSign, GraduationCap, Languages, CalendarClock, ArrowLeft, Loader2, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Section } from "@/components/blocks";
import TutorReviews from "@/components/TutorReviews";
import { toast } from "sonner";
import api from "@/lib/api";

export default function TutorProfile() {
  const { tutorId } = useParams();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/tutors/${tutorId}`).then((r) => setTutor(r.data)).catch(() => setTutor(null)).finally(() => setLoading(false));
  }, [tutorId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please complete all fields."); return; }
    setSubmitting(true);
    try {
      await api.post(`/tutors/${tutorId}/enquiry`, { tutor_id: tutorId, ...form });
      toast.success("Enquiry sent to the tutor!");
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error("Could not send enquiry."); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="py-32 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>;
  if (!tutor) return <Section><p className="text-center text-slate-500">Tutor not found. <Link to="/tutoring" className="text-teal-600 underline">Back to marketplace</Link></p></Section>;

  return (
    <div data-testid="tutor-profile-page">
      <div className="bg-slate-900 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10 mb-4"><Link to="/tutoring" data-testid="tutor-back"><ArrowLeft className="w-4 h-4" /> Back to tutors</Link></Button>
        </div>
      </div>
      <Section className="!pt-10">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              {tutor.photo
                ? <img src={tutor.photo} alt={tutor.name} className="w-40 h-40 rounded-2xl object-cover border border-slate-200" />
                : <div className="w-40 h-40 rounded-2xl bg-slate-100 border border-slate-200 shrink-0" />}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-display text-3xl font-bold text-slate-900">{tutor.name}</h1>
                  {tutor.verified
                    ? <BadgeCheck className="w-6 h-6 text-teal-600" data-testid="tutor-verified-icon" />
                    : tutor.sample
                      ? <Badge className="bg-slate-900/85 text-white hover:bg-slate-900/85" data-testid="tutor-sample-badge"><FlaskConical className="w-3.5 h-3.5 mr-1" /> Sample profile</Badge>
                      : <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid="tutor-new-badge">New tutor</Badge>}
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
                  {tutor.rating
                    ? <span className="flex items-center gap-1" data-testid="tutor-rating"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {tutor.rating} {tutor.review_count ? `from ${tutor.review_count} review${tutor.review_count === 1 ? "" : "s"}` : "rating"}</span>
                    : <span className="text-slate-500" data-testid="tutor-no-reviews">No reviews yet</span>}
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {tutor.location}</span>
                  <span className="flex items-center gap-1"><Wifi className="w-4 h-4" /> {tutor.mode}</span>
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">{tutor.intro}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-10">
              <InfoCard icon={GraduationCap} title="Qualifications" items={tutor.qualifications} />
              <InfoCard icon={Languages} title="Languages" items={tutor.languages} pills />
              <InfoCard icon={GraduationCap} title="Subjects" items={tutor.subjects} pills />
              <InfoCard icon={CalendarClock} title="Availability" items={[tutor.availability, `Year levels: ${tutor.year_levels.join(", ")}`]} />
            </div>

            <TutorReviews tutorId={tutorId} tutorName={tutor.name.split(" ")[0]} />
          </div>

          <div>
            <Card className="p-6 border-slate-200 sticky top-24">
              <div className="flex items-baseline gap-1 mb-1"><DollarSign className="w-5 h-5 text-teal-600" /><span className="text-3xl font-bold text-slate-900">{tutor.rate}</span><span className="text-slate-500">/hour</span></div>
              <p className="text-sm text-slate-500 mb-5">Send a booking enquiry — the tutor will reply to arrange a session.</p>
              <form onSubmit={submit} className="space-y-3" data-testid="tutor-enquiry-form">
                <div><Label htmlFor="te-name">Your name</Label><Input id="te-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="tutor-enquiry-name" /></div>
                <div><Label htmlFor="te-email">Email</Label><Input id="te-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="tutor-enquiry-email" /></div>
                <div><Label htmlFor="te-msg">Message</Label><Textarea id="te-msg" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" placeholder="Subject, year level and preferred times…" data-testid="tutor-enquiry-message" /></div>
                <Button type="submit" disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="tutor-enquiry-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send booking enquiry</>}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
}

function InfoCard({ icon: Icon, title, items, pills }) {
  return (
    <Card className="p-6 border-slate-200">
      <div className="flex items-center gap-2 mb-3"><Icon className="w-5 h-5 text-teal-600" /><h3 className="font-display font-semibold text-slate-900">{title}</h3></div>
      {pills ? (
        <div className="flex flex-wrap gap-1.5">{items.map((i) => <Badge key={i} variant="secondary">{i}</Badge>)}</div>
      ) : (
        <ul className="space-y-1.5 text-sm text-slate-600">{items.map((i) => <li key={i}>{i}</li>)}</ul>
      )}
    </Card>
  );
}
